/**
 * Empty State Components for ScopeOps Platform
 * Provides consistent empty/null state rendering across the application
 */

import React from 'react';
import { 
  FileQuestion, Database, AlertCircle, Search, Inbox, 
  FolderOpen, ListX, PlusCircle, RefreshCw
} from 'lucide-react';

/**
 * Base Empty State Component
 */
const EmptyState = ({ 
  icon: Icon = Inbox,
  title = 'No data',
  description,
  action,
  actionLabel,
  onAction,
  variant = 'default',
  className = '',
  'data-testid': testId = 'empty-state'
}) => {
  const variants = {
    default: {
      container: 'py-12 text-center',
      icon: 'w-12 h-12 text-gray-300 mx-auto mb-4',
      title: 'text-lg font-medium text-gray-600 mb-2',
      description: 'text-sm text-gray-500 max-w-md mx-auto',
    },
    compact: {
      container: 'py-6 text-center',
      icon: 'w-8 h-8 text-gray-300 mx-auto mb-2',
      title: 'text-sm font-medium text-gray-600 mb-1',
      description: 'text-xs text-gray-500',
    },
    inline: {
      container: 'py-4 px-3 flex items-center gap-3',
      icon: 'w-5 h-5 text-gray-400',
      title: 'text-sm text-gray-600',
      description: 'text-xs text-gray-500',
    },
    card: {
      container: 'bg-gray-50 rounded-lg border border-dashed border-gray-200 p-8 text-center',
      icon: 'w-10 h-10 text-gray-300 mx-auto mb-3',
      title: 'text-base font-medium text-gray-600 mb-1',
      description: 'text-sm text-gray-500',
    },
    pane: {
      container: 'h-full flex flex-col items-center justify-center p-6',
      icon: 'w-12 h-12 text-gray-300 mb-4',
      title: 'text-lg font-medium text-gray-600 mb-2',
      description: 'text-sm text-gray-500 text-center max-w-xs',
    }
  };

  const style = variants[variant] || variants.default;

  return (
    <div className={`${style.container} ${className}`} data-testid={testId}>
      <Icon className={style.icon} />
      <h3 className={style.title}>{title}</h3>
      {description && <p className={style.description}>{description}</p>}
      {(action || onAction) && (
        <button
          onClick={action?.onClick || onAction}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
          data-testid="empty-state-action-btn"
        >
          {action?.icon || <PlusCircle className="w-4 h-4" />}
          {actionLabel || action?.label || 'Add Item'}
        </button>
      )}
    </div>
  );
};

// ========================================
// PRESET EMPTY STATES
// ========================================

/**
 * No data found state
 */
export const NoDataState = (props) => (
  <EmptyState
    icon={Database}
    title="No data available"
    description="There's no data to display at this time."
    {...props}
  />
);

/**
 * No results found (after search/filter)
 */
export const NoResultsState = ({ query, onClear, ...props }) => (
  <EmptyState
    icon={Search}
    title="No results found"
    description={query 
      ? `No items match "${query}". Try adjusting your search or filters.`
      : "No items match your current filters."
    }
    actionLabel={onClear ? "Clear Filters" : undefined}
    onAction={onClear}
    {...props}
  />
);

/**
 * No items in list
 */
export const NoItemsState = ({ itemType = 'items', onCreate, ...props }) => (
  <EmptyState
    icon={ListX}
    title={`No ${itemType} yet`}
    description={`Get started by creating your first ${itemType.replace(/s$/, '')}.`}
    actionLabel={`Add ${itemType.replace(/s$/, '')}`}
    onAction={onCreate}
    {...props}
  />
);

/**
 * Empty folder/category
 */
export const EmptyFolderState = ({ folderName, ...props }) => (
  <EmptyState
    icon={FolderOpen}
    title={folderName ? `${folderName} is empty` : "This folder is empty"}
    description="Items will appear here when added."
    {...props}
  />
);

/**
 * Loading failed state
 */
export const LoadFailedState = ({ onRetry, error, ...props }) => (
  <EmptyState
    icon={AlertCircle}
    title="Failed to load"
    description={error?.message || "We couldn't load this content. Please try again."}
    actionLabel="Retry"
    action={{ icon: <RefreshCw className="w-4 h-4" /> }}
    onAction={onRetry}
    {...props}
  />
);

/**
 * Feature not available
 */
export const NotAvailableState = ({ feature, onUpgrade, ...props }) => (
  <EmptyState
    icon={FileQuestion}
    title="Feature not available"
    description={feature 
      ? `${feature} is not available in your current plan.`
      : "This feature is not available in your current plan."
    }
    actionLabel={onUpgrade ? "Upgrade Plan" : undefined}
    onAction={onUpgrade}
    {...props}
  />
);

/**
 * No activity state
 */
export const NoActivityState = (props) => (
  <EmptyState
    icon={Inbox}
    title="No recent activity"
    description="Activity will appear here as changes are made."
    {...props}
  />
);

// ========================================
// ENTITY-SPECIFIC EMPTY STATES
// ========================================

/**
 * No features state
 */
export const NoFeaturesState = ({ onCreate, variant = 'default' }) => (
  <EmptyState
    icon={ListX}
    title="No features found"
    description="Features will appear here when they are added to your projects."
    actionLabel="Add Feature"
    onAction={onCreate}
    variant={variant}
    data-testid="no-features-state"
  />
);

/**
 * No epics state
 */
export const NoEpicsState = ({ onCreate, variant = 'default' }) => (
  <EmptyState
    icon={ListX}
    title="No epics found"
    description="Epics will appear here when they are added to your projects."
    actionLabel="Add Epic"
    onAction={onCreate}
    variant={variant}
    data-testid="no-epics-state"
  />
);

/**
 * No risks state
 */
export const NoRisksState = ({ onCreate, variant = 'default' }) => (
  <EmptyState
    icon={AlertCircle}
    title="No risks identified"
    description="Risks will appear here when they are logged for your projects."
    actionLabel="Log Risk"
    onAction={onCreate}
    variant={variant}
    data-testid="no-risks-state"
  />
);

/**
 * No cost data state (for panes)
 */
export const NoCostDataState = ({ onAddCost, variant = 'compact' }) => (
  <EmptyState
    icon={Database}
    title="No cost data"
    description="Cost information will appear here when added."
    actionLabel={onAddCost ? "Add cost data" : undefined}
    onAction={onAddCost}
    variant={variant}
    data-testid="no-cost-data-state"
  />
);

/**
 * No work items state
 */
export const NoWorkItemsState = ({ itemType = 'work items', variant = 'compact' }) => (
  <EmptyState
    icon={Inbox}
    title={`No ${itemType}`}
    description={`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} will appear here.`}
    variant={variant}
    data-testid="no-work-items-state"
  />
);

export default EmptyState;
